import { useEventListener } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";
import { isPresent, toBoolean } from "#ce/utils/props";
import { isComposing } from "#ce/utils/events";
import { resolveActivatorControl } from "#ce/utils/activator-control";
import { getSlotContent, hasSlotContent } from "#ce/utils/slots";
import { useSelectedTagsMode } from "./useSelectedTagsMode";
import { tAccessibleName } from "#ce/shared/components-i18n";

/** Selected-value presentation and user removal, sharing the picker's selection model. */
export function useSelectedTags(
  props,
  {
    model,
    disabled,
    selectByUser,
    getItemValue,
    getItemTitle,
    findItem,
    host,
    slot,
    announcedStatus,
    trailingSlot = ref(null),
  },
) {
  const enabled = useSelectedTagsMode(props);
  const mutable = computed(() => !disabled.value && !toBoolean(props.readonly));
  const entries = computed(() => model.value.filter((item) => getItemValue(item) != null));
  const required = computed(() => isPresent(props.required));
  const canRemove = computed(() => mutable.value && (!required.value || entries.value.length > 1));
  const canClear = computed(() => mutable.value && !required.value);
  const resolvedTitles = new Map();

  function resolvedTitle(item) {
    const value = getItemValue(item);
    return getItemTitle(findItem(value)) ?? (typeof item === "object" ? getItemTitle(item) : undefined);
  }

  function rememberTitles() {
    if (!enabled.value) {
      resolvedTitles.clear();
      return;
    }
    const values = new Set(entries.value.map((item) => String(getItemValue(item))));
    for (const value of resolvedTitles.keys()) {
      if (!values.has(value)) {
        resolvedTitles.delete(value);
      }
    }
    for (const item of entries.value) {
      const title = resolvedTitle(item);
      if (title != null) {
        resolvedTitles.set(String(getItemValue(item)), String(title));
      }
    }
  }

  watch(() => (enabled.value ? [entries.value, props.items, props.itemTitle, props.itemValue] : null), rememberTitles, {
    immediate: true,
    deep: true,
    flush: "sync",
  });

  const tags = computed(() =>
    enabled.value
      ? entries.value.map((item) => {
          const value = getItemValue(item);
          function handleClose() {
            removeTag(value);
          }
          return {
            onClose: handleClose,
            value,
            label: String(resolvedTitle(item) ?? resolvedTitles.get(String(value)) ?? value),
          };
        })
      : [],
  );
  const showClear = computed(() => canClear.value && toBoolean(props.selectionClearable) && entries.value.length > 0);
  const hasSelectionControls = computed(() => enabled.value || toBoolean(props.selectionClearable));
  const hasTrailingContent = ref(false);
  function readTrailingContent() {
    hasTrailingContent.value = hasSlotContent(trailingSlot);
  }
  useEventListener(trailingSlot, "slotchange", readTrailingContent);
  watch(trailingSlot, readTrailingContent, { immediate: true, flush: "post" });
  const hasSelectionActions = computed(() => showClear.value || hasTrailingContent.value);
  const clearLabel = computed(() => tAccessibleName("selection_clear"));
  const fieldModifiers = computed(() => ({
    "--disabled": hasSelectionControls.value && disabled.value,
    "--selected-tags": enabled.value,
    "--selection-controls": hasSelectionControls.value,
  }));

  function activatorControl() {
    return getSlotContent(slot.value, { flatten: true }).map(resolveActivatorControl).find(Boolean);
  }

  function focusInput() {
    activatorControl()?.focus({ preventScroll: true });
  }

  function focusAfterRemoval(index) {
    const chip = host?.shadowRoot?.querySelectorAll(".sp-quantum-select__tag")[index];
    const control = chip?.closeControlElement;
    if (control) {
      control.focus({ preventScroll: true });
    } else {
      focusInput();
    }
  }

  function announce(message) {
    announcedStatus.value = "";
    nextTick(() => {
      announcedStatus.value = message;
    });
  }

  function removeTag(value, { focus = true } = {}) {
    if (!enabled.value || !canRemove.value) {
      return;
    }
    const index = tags.value.findIndex((tag) => tag.value === value);
    if (index < 0) {
      return;
    }
    const label = tags.value[index].label;
    selectByUser(model.value.filter((item) => getItemValue(item) !== value));
    announce(tAccessibleName("selection_removed", { label }));
    if (focus) {
      function focusNextTag() {
        focusAfterRemoval(index);
      }
      nextTick(focusNextTag);
    }
  }

  function clearSelection(event) {
    event?.stopPropagation();
    if (!showClear.value || !canClear.value) {
      return;
    }
    selectByUser([]);
    announce(tAccessibleName("selection_cleared"));
    nextTick(focusInput);
  }

  function suppressSelectionControlKeys(event) {
    // The list listens on window while open. Selection buttons keep native key activation without
    // those keys also navigating or committing a list row.
    if (event.composedPath().some((node) => node?.hasAttribute?.("data-selection-control"))) {
      if (["Enter", " ", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
        event.stopPropagation();
      }
    }
  }

  function handleBackspace(event) {
    const input = event.composedPath()[0];
    if (
      !enabled.value ||
      !canRemove.value ||
      event.key !== "Backspace" ||
      isComposing(event) ||
      event.defaultPrevented ||
      !input?.matches?.("input, textarea") ||
      (input !== activatorControl() &&
        !event
          .composedPath()
          .some((node) => node?.localName === "sp-search-box" && node.getRootNode() === host?.shadowRoot)) ||
      input.value !== "" ||
      input.disabled ||
      input.readOnly ||
      tags.value.length === 0
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    removeTag(tags.value.at(-1).value, { focus: false });
    if (toBoolean(props.closeOnSelect)) {
      nextTick(focusInput);
    }
  }

  useEventListener(host, "keydown", suppressSelectionControlKeys, { capture: true });
  useEventListener(host, "keydown", handleBackspace);

  return {
    tags,
    selectedTagsEnabled: enabled,
    hasSelectionActions,
    hasSelectionControls,
    canRemove,
    canClear,
    showClear,
    clearLabel,
    fieldModifiers,
    clearSelection,
  };
}
